import React from 'react';
import { FaExclamationCircle } from 'react-icons/fa';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import type { ChatChannel } from '@shared/schemas/chat.schema.js';
import type { ChannelHistory } from '../../contexts/chatReducer';

interface MessageAreaProps {
  channel: ChatChannel;
  /** What the chat holds of this channel. */
  history: ChannelHistory;
  onSendMessage: (content: string) => void;
  onRetryMessage: (messageId: string) => void;
  onRetryJoin: () => void;
  onLoadMore: () => void;
}

export const MessageArea: React.FC<MessageAreaProps> = ({
  channel,
  history,
  onSendMessage,
  onRetryMessage,
  onRetryJoin,
  onLoadMore
}) => {
  return (
    <div className="flex flex-col h-full">
      {/* The channel could not be loaded (refused, or no answer): what is
          held of it stays on screen, with a way to try again */}
      {history.join === 'failed' && (
        <div
          role="alert"
          className="flex-shrink-0 flex items-center gap-3 px-4 py-2 text-sm text-white bg-game-orange/20 border-b border-game-orange"
        >
          <FaExclamationCircle className="flex-shrink-0 text-game-orange" aria-hidden="true" />
          <span className="flex-1">This channel could not be loaded.</span>
          <button
            type="button"
            onClick={onRetryJoin}
            className="font-semibold text-game-golden hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-game-golden rounded"
          >
            Retry
          </button>
        </div>
      )}

      {/* Messages - scrollable container with fixed height */}
      <div className="flex-1 min-h-0">
        <MessageList
          messages={history.messages}
          channelId={channel.id}
          dividerBeforeId={history.dividerBeforeId ?? null}
          join={history.join}
          hasMore={history.hasMore}
          isLoadingMore={history.older === 'loading'}
          loadMoreFailed={history.older === 'failed'}
          onLoadMore={onLoadMore}
          onRetryMessage={onRetryMessage}
        />
      </div>

      {/* Message Input - fixed at bottom */}
      <div className="flex-shrink-0 border-t border-gray-700">
        <MessageInput
          onSendMessage={onSendMessage}
          placeholder={`Message ${channel.type === 'public' ? '#' : ''}${channel.displayName || channel.name}`}
        />
      </div>
    </div>
  );
};
